import { forceUpdate, h, Host } from "@stencil/core";
import { dynamicBulletsHandler } from "./dynamic-bullets-handler";
import { debounce } from "../../../utils/debounce";
import { i18nProvider } from "@trumpf/ux.i18n";
export class SliderPagination {
    constructor() {
        this.paginationOffsetTop = 0;
        this.paginationOffsetLeft = 0;
        this.isDynamic = false;
        this.isBeginning = false;
        this.isEnd = false;
        this.showPagination = false;
        this.sliderId = "";
        this.withoutArrows = false;
        this.bottomPagination = false;
        this.anchorPagination = false;
        this.customPagination = "";
        this.label = false;
        this.progress = false;
    }
    baseClass = "ux-slider-pagination";
    bulletObserver;
    paginationElement;
    bulletsContainer;
    /** event to go to next slide */
    nextSlide;
    /** event to go to previous slide */
    previousSlide;
    handleNextSlide() {
        this.nextSlide.emit(this.sliderId);
    }
    handlePreviousSlide() {
        this.previousSlide.emit(this.sliderId);
    }
    changeAnchorPosition(event) {
        if (event.detail.id === this.sliderId) {
            this.handleAnchorPosition();
            const isDynamic = dynamicBulletsHandler(this.bulletsContainer);
            if (isDynamic !== this.isDynamic) {
                this.isDynamic = isDynamic;
            }
            if (event.detail.event === "checkSliderStartEnd") {
                this.isBeginning = event.detail.isBeginning;
                this.isEnd = event.detail.isEnd;
            }
        }
    }
    handleAnchorPosition = () => {
        if (!this.anchorPagination) {
            return;
        }
        const slider = this.paginationElement.parentNode;
        // @ts-expect-error Check for typing
        const activeSlide = slider.querySelector(`.ux-slider-slide-active`);
        if (activeSlide === null) {
            // @ts-expect-error Check for typing
            slider.querySelector(`.ux-slider-slide`);
        }
        if (activeSlide === null) {
            return;
        }
        // @ts-expect-error Check for typing
        const anchorElement = activeSlide.querySelector(".ux-slider-inline-pagination-anchor");
        if (anchorElement === null) {
            return;
        }
        let additionalOffset = 0;
        const parentNode = anchorElement.parentNode;
        if (anchorElement.parentNode !== activeSlide && parentNode.clientHeight > anchorElement.offsetTop) {
            additionalOffset = parentNode.offsetTop;
        }
        const offsetTop = anchorElement.offsetTop + Math.abs(additionalOffset);
        let additionalOffsetLeft = 0;
        if (anchorElement.parentNode !== activeSlide) {
            additionalOffsetLeft = anchorElement.parentNode.offsetLeft;
        }
        const offsetLeft = anchorElement.offsetLeft + additionalOffsetLeft + anchorElement.offsetWidth / 2;
        if (this.paginationOffsetTop === offsetTop && this.paginationOffsetLeft === offsetLeft) {
            return;
        }
        this.paginationOffsetTop = offsetTop;
        this.paginationOffsetLeft = offsetLeft;
    };
    componentWillLoad() {
        i18nProvider.subscribe(() => {
            forceUpdate(this);
        });
    }
    componentDidLoad() {
        this.handleAnchorPosition();
        this.bulletObserver = new MutationObserver(() => {
            this.showPagination = this.bulletsContainer.children.length > 1;
        });
        this.bulletObserver.observe(this.bulletsContainer, { childList: true });
    }
    onResize = debounce(() => this.handleAnchorPosition(), 150);
    connectedCallback() {
        if (this.bottomPagination) {
            return;
        }
        window.addEventListener("resize", this.onResize);
    }
    disconnectedCallback() {
        if (this.bottomPagination) {
            return;
        }
        window.removeEventListener("resize", this.onResize);
        this.bulletObserver.disconnect();
    }
    render() {
        const { baseClass, bottomPagination, anchorPagination, withoutArrows, paginationOffsetTop, paginationOffsetLeft, isDynamic, label, progress } = this;
        const classes = [baseClass];
        if (bottomPagination) {
            classes.push(baseClass + "--bottom");
        }
        else if (anchorPagination) {
            classes.push(baseClass + "--anchor");
        }
        if (label) {
            classes.push(baseClass + "--label");
        }
        const anchorStyles = anchorPagination
            ? {
                top: paginationOffsetTop > 0 ? `${paginationOffsetTop}px` : "100%",
                left: paginationOffsetLeft > 0 ? `${paginationOffsetLeft}px` : "50%"
            }
            : null;
        const display = this.showPagination ? undefined : "none";
        const { t } = i18nProvider;
        return (h(Host, { key: 'a296b4193fff277f64edfd11b10fb032d49dd8ec', class: classes.join(" "), style: { ...anchorStyles, display } }, !withoutArrows && (h("ux-icon", { key: '2208257677b00583a44c0daf165a8769ddd6e159', name: "chevron-right", weight: "normal", class: `${baseClass}__navigation-arrow ${baseClass}__navigation-arrow--prev`, onClick: this.handlePreviousSlide.bind(this), role: "button", "aria-label": t("slider.prevButton.label"), ...(this.isBeginning && { "aria-disabled": "true" }) })), h("div", { key: '40a3317d90aff67d24e46ea8ad62b55caf9fddaf', class: {
                [`${baseClass}__container`]: true,
                [`${baseClass}__container--dynamic`]: isDynamic,
                [`${baseClass}__container--label`]: label,
                [`${baseClass}__container--progress`]: progress
            },
            // @ts-expect-error Check for typing
            ref: (el) => (this.bulletsContainer = el) }), !withoutArrows && (h("ux-icon", { key: '768a491c901e1caed7e820ff565b627aebdab64c', name: "chevron-right", weight: "normal", class: `${baseClass}__navigation-arrow ${baseClass}__navigation-arrow--next`, onClick: this.handleNextSlide.bind(this), role: "button", "aria-label": t("slider.nextButton.label"), ...(this.isEnd && { "aria-disabled": "true" }) }))));
    }
    static get is() { return "ux-slider-pagination"; }
    static get originalStyleUrls() {
        return {
            "$": ["slider-pagination.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["slider-pagination.css"]
        };
    }
    static get properties() {
        return {
            "sliderId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the unique id of the reference slider - this will be set automatically by the ux-slider"
                },
                "attribute": "slider-id",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "withoutArrows": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the navigation bullets will not have side arrows"
                },
                "attribute": "without-arrows",
                "reflect": false,
                "defaultValue": "false"
            },
            "bottomPagination": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the navigation will take the necessary styles to be the bottom navigation"
                },
                "attribute": "bottom-pagination",
                "reflect": false,
                "defaultValue": "false"
            },
            "anchorPagination": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the navigation will take the necessary styles to be the anchor navigation"
                },
                "attribute": "anchor-pagination",
                "reflect": false,
                "defaultValue": "false"
            },
            "customPagination": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "manages the pagination of custom slider types"
                },
                "attribute": "custom-pagination",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "label": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider will show the slide label inside the slide pagination"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "false"
            },
            "progress": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the slider will show the slide progress inside the slide pagination"
                },
                "attribute": "progress",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "paginationOffsetTop": {},
            "paginationOffsetLeft": {},
            "isDynamic": {},
            "isBeginning": {},
            "isEnd": {},
            "showPagination": {}
        };
    }
    static get events() {
        return [{
                "method": "nextSlide",
                "name": "uxSliderNextSlide",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "event to go to next slide"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "previousSlide",
                "name": "uxSliderPreviousSlide",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "event to go to previous slide"
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "paginationElement"; }
    static get listeners() {
        return [{
                "name": "uxSliderUpdatePagination",
                "method": "changeAnchorPosition",
                "target": "document",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=slider-pagination.js.map
